import { useEffect, useState } from "react";
import PDFViewer from "./PDFViewer";

interface TestEditorHeaderProps {
  testName: string;
  onNameChange: (name: string) => void;
  onSave: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onBack: () => void;
  canUndo: boolean;
  canRedo: boolean;
  isSaving: boolean;
  isDirty: boolean;
  pdfUrl?: string;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  isPublished?: boolean;
  onPublishChange?: (isPublished: boolean) => void;
}

export default function TestEditorHeader({
  testName,
  onNameChange,
  onSave,
  onUndo,
  onRedo,
  onBack,
  canUndo,
  canRedo,
  isSaving,
  isDirty,
  pdfUrl,
  onExpandAll,
  onCollapseAll,
  isPublished = false,
  onPublishChange,
}: TestEditorHeaderProps) {
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(testName);
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);

  // Update local state when prop changes
  useEffect(() => {
    setNameValue(testName);
  }, [testName]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNameValue(e.target.value);
  };

  const handleNameBlur = () => {
    setEditingName(false);
    if (nameValue.trim() !== testName) {
      onNameChange(nameValue.trim());
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setEditingName(false);
      if (nameValue.trim() !== testName) {
        onNameChange(nameValue.trim());
      }
    } else if (e.key === "Escape") {
      setEditingName(false);
      setNameValue(testName);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex flex-col sm:flex-row justify-between items-start sm:items-center shadow-md">
      <div className="flex flex-col gap-2 w-full">
        {/* Main fixed container */}
        <div className="flex items-center mb-4 sm:mb-0 w-full">
          <button
            onClick={onBack}
            className="mr-3 text-gray-500 hover:text-gray-700"
            aria-label="Back"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              ></path>
            </svg>
          </button>

          {editingName ? (
            <input
              type="text"
              value={nameValue}
              onChange={handleNameChange}
              onBlur={handleNameBlur}
              onKeyDown={handleNameKeyDown}
              className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-slate-100 border-b border-dashed border-gray-400 focus:outline-none focus:border-indigo-500 bg-transparent w-[calc(100%-70px)]"
              autoFocus
            />
          ) : (
            <h1
              className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold cursor-pointer hover:text-indigo-600"
              onClick={() => setEditingName(true)}
            >
              {testName}
              <span className="ml-2 text-gray-400 text-sm">
                <svg
                  className="w-4 h-4 inline"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  ></path>
                </svg>
              </span>
            </h1>
          )}
        </div>

        <div className="flex items-center justify-end space-x-2">
          {/* Published Status Switch */}
          {onPublishChange && (
            <div className="flex items-center mr-2">
              {/* Start */}
              <div className="flex items-center">

                <div className="text-sm text-gray-400 dark:text-gray-500 italic mr-2">{isPublished ? 'Published' : 'Draft'}</div>
                <div className="form-switch">
                  <input type="checkbox" id="switch-1" className="sr-only" checked={isPublished} onChange={() => onPublishChange(!isPublished)} />
                  <label className="bg-gray-400 dark:bg-gray-700" htmlFor="switch-1">
                    <span className="bg-white shadow-sm" aria-hidden="true"></span>
                    <span className="sr-only">Published status</span>
                  </label>
                </div>
              </div>
              {/* End */}
            </div>
          )}

          {/* View PDF */}
          <button
            onClick={() => setIsPdfViewerOpen(true)}
            className="px-3 py-2 rounded-md flex items-center text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            title="View PDF"
          >
            <svg
              className="shrink-0 fill-current text-gray-400 dark:text-gray-500 mr-2"
              width="16"
              height="16"
              viewBox=" 0 0 16 16"
            >
              <path d="M0 4a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v8a4 4 0 0 1-4 4H4a4 4 0 0 1-4-4V4Zm2 0v8a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Zm9 1a1 1 0 0 1 0 2H5a1 1 0 1 1 0-2h6Zm0 4a1 1 0 0 1 0 2H5a1 1 0 1 1 0-2h6Z" />
            </svg>
            View PDF
          </button>

          {/* Expand All Sections */}
          {onExpandAll && (
            <button
              onClick={onExpandAll}
              className="px-3 py-2 rounded-md flex items-center text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              title="Expand All Sections"
            >
              <svg
                className="shrink-0 fill-current text-gray-400 dark:text-gray-500 mr-2"
                width="16"
                height="16"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              Expand All
            </button>
          )}

          {/* Collapse All Sections */}
          {onCollapseAll && (
            <button
              onClick={onCollapseAll}
              className="px-3 py-2 rounded-md flex items-center text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              title="Collapse All Sections"
            >
              <svg
                className="shrink-0 fill-current text-gray-400 dark:text-gray-500 mr-2"
                width="16"
                height="16"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
                  clipRule="evenodd"
                />
              </svg>
              Collapse All
            </button>
          )}

          <button
            onClick={onUndo}
            disabled={!canUndo}
            className={`px-3 py-2 rounded-md flex items-center ${canUndo
              ? "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              : "text-gray-400 dark:text-gray-600 cursor-not-allowed"
              }`}
            title="Undo"
          >
            <svg
              className="w-5 h-5 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
              ></path>
            </svg>
            Undo
          </button>

          <button
            onClick={onRedo}
            disabled={!canRedo}
            className={`px-3 py-2 rounded-md flex items-center ${canRedo
              ? "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
              : "text-gray-400 dark:text-gray-600 cursor-not-allowed"
              }`}
            title="Redo"
          >
            <svg
              className="w-5 h-5 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M14 10h-4a8 8 0 00-8 8v2M14 10l-6 6m6-6l-6-6"
              ></path>
            </svg>
            Redo
          </button>

          <button
            onClick={onSave}
            disabled={isSaving || !isDirty}
            className={`px-4 py-2 rounded-md flex items-center ${isSaving || !isDirty
              ? "bg-indigo-400 cursor-not-allowed"
              : "bg-indigo-600 hover:bg-indigo-700"
              } text-white`}
          >
            {isSaving ? (
              <>
                Saving...
              </>
            ) : (
              <>
                Save
              </>
            )}
          </button>
        </div>
      </div>

      {/* PDF Viewer Modal */}
      <PDFViewer
        pdfUrl={pdfUrl}
        isOpen={isPdfViewerOpen}
        onClose={() => setIsPdfViewerOpen(false)}
      />
    </div>
  );
}
